/**
 * Enhanced AI recommendation prompt for the legal-services RFP evaluation engine.
 *
 * This is an improved alternative to `initialAiPrompt` in `InitialAiPrompt.tsx`.
 * It keeps the same dynamic placeholder convention (`<legal_services_providerX_..._value>`)
 * and the same per-section conditional rendering, so it is a drop-in replacement,
 * while adding:
 *   - a clearer evaluator persona and deterministic rules,
 *   - an explicit 0-5 scoring scale and ECW-weighting methodology,
 *   - strict valid-JSON output requirements (no prose outside JSON),
 *   - edge-case handling (ties, missing data, fewer than 5 bidders).
 */

const enhancedResponseObject = (rfpListById) => {
  const criterion = (label, scorePlaceholder) =>
    `      "${label}": {
        "score": ${scorePlaceholder},
        "weight": <${label}_ecw_value>,
        "weighted_score": <${label}_score_times_weight>,
        "explanation": "Concise, evidence-based reason for this score, referencing the provider's submitted value."
      },\n`;

  const pricing = (rfpListById?.detail && rfpListById?.detail[0]?.pricing[0] && criterion('pricing', '<legal_services_providerX_pricing_score>')) || '';

  const assumptions =
    (rfpListById?.detail &&
      (rfpListById?.detail?.[0]?.pricing?.[0]?.assumptions_exclusions?.add_assumption ||
        rfpListById?.detail?.[0]?.pricing?.[0]?.assumptions_exclusions?.not_add_assumption) &&
      criterion('assumptions_exclusions', '<legal_services_providerX_assumptions_score>')) ||
    '';

  const keyTeamMember =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.key_team_members?.value &&
      criterion('key_team_members', '<legal_services_providerX_key_team_score>')) ||
    '';

  const staffing =
    (rfpListById?.detail && rfpListById?.detail[0]?.other_key_info[0]?.staffing?.value && criterion('staffing', '<legal_services_providerX_staffing_score>')) ||
    '';

  const diversity =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.diversity?.value &&
      criterion('diversity', '<legal_services_providerX_diversity_score>')) ||
    '';

  const credentials =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.credentials?.value &&
      criterion('credentials', '<legal_services_providerX_credentials_score>')) ||
    '';

  const disaggregation =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.disaggregation?.value &&
      criterion('disaggregation', '<legal_services_providerX_disaggregation_score>')) ||
    '';

  const usp = (rfpListById?.detail && rfpListById?.detail[0]?.other_key_info[0]?.usp?.value && criterion('usp', '<legal_services_providerX_usp_score>')) || '';

  const video =
    (rfpListById?.detail && rfpListById?.detail[0]?.other_key_info[0]?.video?.value && criterion('video', '<legal_services_providerX_video_score>')) || '';

  const other =
    (rfpListById?.detail && rfpListById?.detail[0]?.other_key_info[0]?.other?.value && criterion('other', '<legal_services_providerX_other_score>')) || '';

  return { pricing, assumptions, keyTeamMember, staffing, diversity, credentials, disaggregation, usp, video, other };
};

export const initialAiPrompt = (rfpListById) => {
  const pricingSection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.pricing[0] &&
      `1. Pricing (weight: <pricing_ecw_value>):
Evaluate competitiveness, clarity, and value for money of each firm's pricing model. Compare like-for-like across firms; a lower total is not automatically better if scope or assumptions differ.
Provider values:
- Legal services provider 1: <legal_services_provider1_pricing_value>
- Legal services provider 2: <legal_services_provider2_pricing_value>
- Legal services provider 3: <legal_services_provider3_pricing_value>
- Legal services provider 4: <legal_services_provider4_pricing_value>
- Legal services provider 5: <legal_services_provider5_pricing_value>
Consider: currency, pricing model (Estimate, Fixed Fee, Success Fee, Abort Discount), total amount and estimate basis.`) ||
    '';

  const assumptionsAndExclusionsSection =
    (rfpListById?.detail &&
      (rfpListById?.detail?.[0]?.pricing?.[0]?.assumptions_exclusions?.add_assumption ||
        rfpListById?.detail?.[0]?.pricing?.[0]?.assumptions_exclusions?.not_add_assumption) &&
      `2. Assumptions & Exclusions (weight: <assumptions_ecw_value>):
Evaluate the clarity, relevance, and risk implications of each firm's assumptions and exclusions. Penalise vague or open-ended exclusions that shift risk to the client.
Provider values:
- Legal services provider 1: <legal_services_provider1_assumptions_value>
- Legal services provider 2: <legal_services_provider2_assumptions_value>
- Legal services provider 3: <legal_services_provider3_assumptions_value>
- Legal services provider 4: <legal_services_provider4_assumptions_value>
- Legal services provider 5: <legal_services_provider5_assumptions_value>
Consider: travel expectations, tax-advice scope, coordination with local counsel, and any other assumptions or exclusions.`) ||
    '';

  const keyTeamSection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.key_team_members?.value &&
      `3. Key Team Members (weight: <keyteam_member_ecw_value>):
Assess the qualifications, seniority, relevant experience, and project roles of the proposed team.
Provider values:
- Legal services provider 1: <legal_services_provider1_keyteam_member_value>
- Legal services provider 2: <legal_services_provider2_keyteam_member_value>
- Legal services provider 3: <legal_services_provider3_keyteam_member_value>
- Legal services provider 4: <legal_services_provider4_keyteam_member_value>
- Legal services provider 5: <legal_services_provider5_keyteam_member_value>
Consider: title, role, years of experience, project role, and location of the lead partner.`) ||
    '';

  const staffingSection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.staffing?.value &&
      `4. Staffing (weight: <staffing_ecw_value>):
Evaluate the staffing model and project-management capability: capacity, continuity, and the appropriateness of the leverage model.
Provider values:
- Legal services provider 1: <legal_services_provider1_staffing_value>
- Legal services provider 2: <legal_services_provider2_staffing_value>
- Legal services provider 3: <legal_services_provider3_staffing_value>
- Legal services provider 4: <legal_services_provider4_staffing_value>
- Legal services provider 5: <legal_services_provider5_staffing_value>`) ||
    '';

  const diversitySection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.diversity?.value &&
      `5. Diversity (weight: <diversity_ecw_value>):
Evaluate the firm's commitment to diversity and inclusion through concrete initiatives and policies, not statements of intent alone.
Provider values:
- Legal services provider 1: <legal_services_provider1_diversity_value>
- Legal services provider 2: <legal_services_provider2_diversity_value>
- Legal services provider 3: <legal_services_provider3_diversity_value>
- Legal services provider 4: <legal_services_provider4_diversity_value>
- Legal services provider 5: <legal_services_provider5_diversity_value>`) ||
    '';

  const credentialsSection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.credentials?.value &&
      `6. Credentials (weight: <credentials_ecw_value>):
Assess the firm's credentials ordered by relevance and recency, and any supporting evidence of comparable matter experience.
Provider values:
- Legal services provider 1: <legal_services_provider1_credentials_value>
- Legal services provider 2: <legal_services_provider2_credentials_value>
- Legal services provider 3: <legal_services_provider3_credentials_value>
- Legal services provider 4: <legal_services_provider4_credentials_value>
- Legal services provider 5: <legal_services_provider5_credentials_value>`) ||
    '';

  const disaggregationSection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.disaggregation?.value &&
      `7. Disaggregation (weight: <disaggregation_ecw_value>):
Analyse the disaggregation of services: proposed outsourcing strategy, use of alternative resourcing, and integration of legal-technology solutions.
Provider values:
- Legal services provider 1: <legal_services_provider1_disaggregation_value>
- Legal services provider 2: <legal_services_provider2_disaggregation_value>
- Legal services provider 3: <legal_services_provider3_disaggregation_value>
- Legal services provider 4: <legal_services_provider4_disaggregation_value>
- Legal services provider 5: <legal_services_provider5_disaggregation_value>`) ||
    '';

  const uspSection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.usp?.value &&
      `8. USP (weight: <usp_ecw_value>):
Evaluate each provider's Unique Selling Proposition (max 600 characters): how clearly and credibly it differentiates the firm for this matter.
Provider values:
- Legal services provider 1: <legal_services_provider1_usp_value>
- Legal services provider 2: <legal_services_provider2_usp_value>
- Legal services provider 3: <legal_services_provider3_usp_value>
- Legal services provider 4: <legal_services_provider4_usp_value>
- Legal services provider 5: <legal_services_provider5_usp_value>`) ||
    '';

  const videoSection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.video?.value &&
      `9. Video (weight: <video_ecw_value>):
Assess the supplementary video (max 2 min) provided under "Other key information" for clarity, relevance, and the additional insight it adds beyond the written submission.
Provider values:
- Legal services provider 1: <legal_services_provider1_video_value>
- Legal services provider 2: <legal_services_provider2_video_value>
- Legal services provider 3: <legal_services_provider3_video_value>
- Legal services provider 4: <legal_services_provider4_video_value>
- Legal services provider 5: <legal_services_provider5_video_value>`) ||
    '';

  const otherSection =
    (rfpListById?.detail &&
      rfpListById?.detail[0]?.other_key_info[0]?.other?.value &&
      `10. Other (weight: <other_ecw_value>):
Capture any additional insight not covered by the categories above that is material to the selection decision.
Provider values:
- Legal services provider 1: <legal_services_provider1_other_value>
- Legal services provider 2: <legal_services_provider2_other_value>
- Legal services provider 3: <legal_services_provider3_other_value>
- Legal services provider 4: <legal_services_provider4_other_value>
- Legal services provider 5: <legal_services_provider5_other_value>`) ||
    '';

  const responses = enhancedResponseObject(rfpListById);
  const criteriaScoresBlock = `${responses.pricing}${responses.assumptions}${responses.keyTeamMember}${responses.staffing}${responses.diversity}${responses.credentials}${responses.disaggregation}${responses.usp}${responses.video}${responses.other}`;

  const updatedPrompt = `# Role
You are an impartial procurement analyst for a corporate legal team. Your job is to evaluate competing law-firm (legal services provider) bids against weighted criteria and recommend exactly ONE winning bidder, with clear, defensible reasons for selecting it and for rejecting every other bidder.

# Business Context
- Objective: help the corporate legal team select the single most suitable law firm from the bidding pool for this Request for Proposal (RFP), maximising value for money while managing legal and commercial risk.
- Evaluation methodology: each firm is scored against the same consolidated criteria; every criterion carries an Evaluation Criteria Weighting (ECW) set by the client, so higher-weighted criteria have proportionally more influence on the final decision.
- Legal and commercial considerations: assess suitability for the matter, adequacy of the proposed team's expertise, clarity of pricing, and the risk implications of stated assumptions and exclusions (e.g. scope shifted back to the client).
- Scoring expectations: be objective, consistent, and evidence-based; reward firms that directly address the RFP requirements and penalise vague, incomplete, or non-responsive submissions. Do not favour the lowest price if scope or quality differs.
- Only the criteria that appear in the "Consolidated Evaluation Criteria" section below apply to this RFP; ignore criteria not shown.

# Bidders Under Evaluation
- Legal services provider 1: <legal_services_provider1_name>
- Legal services provider 2: <legal_services_provider2_name>
- Legal services provider 3: <legal_services_provider3_name>
- Legal services provider 4: <legal_services_provider4_name>
- Legal services provider 5: <legal_services_provider5_name>
Only evaluate bidders whose name is populated. Ignore any provider slot left blank.

# Scoring Methodology (follow exactly)
1. For each bidder, score every criterion below on an INTEGER-or-one-decimal scale from 0 to 5:
   - 0 = missing/non-responsive, 1-2 = weak, 3 = acceptable, 4 = strong, 5 = outstanding.
2. Each criterion has an Evaluation Criteria Weighting (ECW). Compute weighted_score = score x weight for that criterion.
3. total_score = sum of all weighted_score values for that bidder.
4. final_score = total_score normalised back onto the 0-5 scale (i.e. total_score divided by the sum of all applicable weights). Round final_score to 2 decimals; it MUST stay within 0-5.
5. Rank bidders by final_score, highest first. The top-ranked bidder is the winner.
6. Score strictly from the evidence in the provided values. If a value is absent or says "N/A", assign score 0 and state that in the explanation. Do not invent facts.
7. Tie-breaker: if two bidders share the same final_score, prefer the one with the higher Pricing weighted_score; if still tied, the higher Key Team Members weighted_score.

# Scoring Calibration (few-shot anchors)
Use these illustrative examples ONLY to calibrate how you assign scores and write explanations. They are not real bidders and must not appear in your output.
- Strong submission (score 5): "Fixed Fee of USD 120,000 covering the full scope, with a clearly itemised breakdown and an abort discount. Directly addresses the RFP and leaves no scope ambiguity." Explanation should cite the specific evidence that justifies the high score.
- Acceptable submission (score 3): "Estimate of USD 150,000 but the basis of the estimate is only partly explained and two workstreams are left open-ended." Explanation should note both the strengths and the gaps.
- Weak submission (score 1-2): "Provides a headline rate only, with broad exclusions that shift tax and local-counsel coordination back to the client." Explanation should identify the risk and why it lowers the score.
- Missing/non-responsive (score 0): value is absent or "N/A". Explanation must state that no information was provided.

# Consolidated Evaluation Criteria
${pricingSection}
${assumptionsAndExclusionsSection}
${keyTeamSection}
${staffingSection}
${diversitySection}
${credentialsSection}
${disaggregationSection}
${uspSection}
${videoSection}
${otherSection}

# Dynamic Input Parameters
The following are populated per RFP and must drive the evaluation:
- scope_of_work_items: subset of the consolidated criteria applicable to this RFP.
- pricing_model: pricing model proposed by each firm (Estimate, Fixed Fee, Success Fee, Abort Discount, etc.).
- assumptions: assumptions and exclusions stated by each firm.
- key_team_members: proposed team with qualifications and roles.
- diversity_policies: diversity policies and initiatives.
- credentials: credentials and other key information relevant to the RFP.
- bidders_count: total number of participating firms.

# Output Requirements (STRICT)
- Respond with a SINGLE valid JSON object and NOTHING else. No markdown, no code fences, no commentary before or after.
- Use only the criteria keys that appear in the template below (these reflect the criteria applicable to this RFP).
- Include EVERY non-winning bidder in "rejected_bidders".
- Every "explanation", "reason_1" and "reason_2" must be a complete, properly quoted JSON string.
- All scores are numbers (not strings) in the range 0-5.

# Output Template
{
  "bidders_count": <bidders_count>,
  "winning_bidder": {
    "law_firm_name": "Winning Firm Name",
    "final_score": <final_score_0_to_5>,
    "total_score": <total_weighted_score>,
    "criteria_scores": {
${criteriaScoresBlock}    },
    "justification": {
      "reason_1": "First key, evidence-based reason this firm was selected.",
      "reason_2": "Second key, evidence-based reason this firm was selected."
    }
  },
  "rejected_bidders": [
    {
      "law_firm_name": "Rejected Firm Name",
      "final_score": <final_score_0_to_5>,
      "total_score": <total_weighted_score>,
      "criteria_scores": {
${criteriaScoresBlock}    },
      "justification": {
        "reason_1": "First key reason this firm was not selected, referencing the gap versus the winner.",
        "reason_2": "Second key reason this firm was not selected."
      }
    }
  ]
}
`;

  return updatedPrompt.replace(/(^[ \t]*\n)/gm, '');
};
